import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./dashboard.scss";
import "../../assets/css/dashboard.scss";
import {
  TotalProjects,
  ActiveProjects,
  TotalWorkers,
  UniSvg,
  TotalOrganizations,
  TotalManagers,
} from "../../assets/icons";
import { Spin } from "antd";
import ModernPieChart from "./ModernPieChart";
import TopOrganizationsChart from "./TopOrganizationsChart";
import SuperAdminRevenueChart from "./SuperAdminRevenueChart";
import { getProjectColor } from "../../utils/colors";
import {
  getSuperAdminDashboard,
  getAllProjectsSuperAdmin,
  getAllOrganizations,
  getTopOrganizations,
  getAllManagersSuperAdmin,
  getAllWorkersSuperAdmin,
  getSubscriptionStats,
} from "../../services/service";
import { getOrganizationDisplayPlan } from "../../utils/subscriptionPlanDisplay";

/* ─────────────────────────────────────────────
   Organisation Detail Drawer
───────────────────────────────────────────── */
const OrgModal = ({ org, onClose, onViewFull }) => {
  if (!org) return null;

  const initials = org.name?.substring(0, 2).toUpperCase() || "OR";
  const isActive = org.status === "Active";

  const planMeta = {
    label: org.planTitle || "Basic",
    bg: "#EFF6FF",
    color: "#1E3A8A",
  };

  const infoRows = [
    {
      label: "Manager",
      value: org.managerName || "N/A",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1E3A8A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
    {
      label: "Total Projects",
      value: org.projectCount ?? 0,
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1E3A8A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      ),
    },
    {
      label: "Subscription Plan",
      value: planMeta.label,
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ),
      highlight: true,
    },
    {
      label: "Account Status",
      value: org.status || "N/A",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={isActive ? "#10B981" : "#EF4444"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          {isActive
            ? <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>
            : <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>
          }
        </svg>
      ),
      valueColor: isActive ? "#10B981" : "#EF4444",
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(15,23,42,0.35)",
          backdropFilter: "blur(2px)",
          zIndex: 1000,
          animation: "drawerFadeIn 0.2s ease",
        }}
      />

      {/* Drawer panel — slides in from right */}
      <div
        style={{
          position: "fixed",
          top: 0, right: 0, bottom: 0,
          width: "min(400px, 95vw)",
          background: "#fff",
          zIndex: 1001,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-8px 0 40px rgba(30,58,138,0.14)",
          fontFamily: "Montserrat, sans-serif",
          animation: "drawerSlideIn 0.28s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        {/* ── Header ── */}
        <div style={{ background: "#1E3A8A", padding: "24px 24px 28px", flexShrink: 0 }}>
          {/* Top row: label + close */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
            <span style={{ color: "rgba(255,255,255,0.55)", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Organization Details
            </span>
            <button
              onClick={onClose}
              style={{
                background: "rgba(255,255,255,0.12)", border: "none",
                borderRadius: 8, width: 30, height: 30,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "rgba(255,255,255,0.7)", fontSize: 18, cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.22)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
            >×</button>
          </div>

          {/* Avatar + name row */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 58, height: 58, borderRadius: 14, flexShrink: 0,
              background: "linear-gradient(135deg,#F97316,#EA580C)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: 20, fontWeight: 800,
              boxShadow: "0 4px 16px rgba(249,115,22,0.5)",
            }}>
              {initials}
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, color: "#fff", fontSize: 17, fontWeight: 700, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {org.name}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                {/* Status badge */}
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  background: isActive ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)",
                  color: isActive ? "#6EE7B7" : "#FCA5A5",
                  fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 20,
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: isActive ? "#6EE7B7" : "#FCA5A5", display: "inline-block" }} />
                  {org.status}
                </span>
                {/* Plan badge */}
                <span style={{
                  background: "rgba(249,115,22,0.2)", color: "#FED7AA",
                  fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 20,
                  textTransform: "capitalize",
                }}>
                  {planMeta.label}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 24px" }}>

          {/* Section: Info */}
          <p style={{ margin: "20px 0 10px", fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Overview
          </p>

          <div style={{ background: "#F8FAFF", borderRadius: 12, border: "1px solid #E8EEFF", overflow: "hidden" }}>
            {infoRows.map((row, i) => (
              <div
                key={row.label}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "13px 16px",
                  borderBottom: i < infoRows.length - 1 ? "1px solid #EEF2FF" : "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: "#fff", border: "1px solid #E8EEFF",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    {row.icon}
                  </div>
                  <span style={{ fontSize: 13, color: "#6B7280", fontWeight: 500 }}>{row.label}</span>
                </div>
                <span style={{
                  fontSize: 13, fontWeight: 700,
                  color: row.valueColor || "#1E3A8A",
                  textTransform: "capitalize",
                  maxWidth: 160, textAlign: "right",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>

          {/* Section: Quick stats */}
          <p style={{ margin: "20px 0 10px", fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Quick Stats
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 24 }}>
            {[
              { label: "Projects", value: org.projectCount ?? 0, bg: "#EFF6FF", color: "#1E3A8A" },
              { label: "Status", value: org.status, bg: isActive ? "#ECFDF5" : "#FEF2F2", color: isActive ? "#059669" : "#DC2626" },
            ].map(card => (
              <div key={card.label} style={{
                background: card.bg, borderRadius: 12, padding: "16px",
                display: "flex", flexDirection: "column", gap: 4,
              }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: card.color }}>{card.value}</span>
                <span style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{card.label}</span>
              </div>
            ))}
          </div>

        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: "16px 24px 24px", borderTop: "1px solid #F3F4F6",
          display: "flex", flexDirection: "column", gap: 10, flexShrink: 0,
        }}>
          <button
            onClick={() => onViewFull(org.id)}
            style={{
              width: "100%", padding: "13px 0", borderRadius: 10, border: "none",
              background: "#F97316", color: "#fff",
              fontWeight: 700, fontSize: 14, fontFamily: "Montserrat, sans-serif",
              cursor: "pointer", transition: "background 0.15s, transform 0.12s",
              boxShadow: "0 4px 14px rgba(249,115,22,0.35)",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#EA580C"; e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#F97316"; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            Open in Subscription Management
          </button>
          <button
            onClick={onClose}
            style={{
              width: "100%", padding: "11px 0", borderRadius: 10,
              border: "1.5px solid #E5E7EB", background: "#fff",
              color: "#6B7280", fontWeight: 600, fontSize: 13,
              fontFamily: "Montserrat, sans-serif", cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#F9FAFB"; e.currentTarget.style.borderColor = "#D1D5DB"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#E5E7EB"; }}
          >
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes drawerFadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes drawerSlideIn {
          from { transform: translateX(100%) }
          to   { transform: translateX(0) }
        }
      `}</style>
    </>
  );
};
/* ─────────────────────────────────────────────
   Main Dashboard Component
───────────────────────────────────────────── */
const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [statsData, setStatsData] = useState({
    total_organizations: 0,
    total_projects: 0,
    total_managers: 0,
    total_workers: 0,
  });
  const [activeProjects, setActiveProjects] = useState([]);
  const [activeOrganizations, setActiveOrganizations] = useState([]);
  const [topOrganizations, setTopOrganizations] = useState([]);
  const [projectsChartData, setProjectsChartData] = useState({
    totalProjects: 0,
    activeProjects: 0,
    inactiveProjects: 0,
    completedProjects: 0,
  });
  const [organizationsChartData, setOrganizationsChartData] = useState({
    totalOrganizations: 0,
    activeOrganizations: 0,
    inactiveOrganizations: 0,
  });
  const [organizationsPagination, setOrganizationsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [projectsPagination, setProjectsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [subscriptionStatsLoading, setSubscriptionStatsLoading] = useState(false);

  // ── Organisation modal state ──
  const [selectedOrg, setSelectedOrg] = useState(null);

  useEffect(() => {
    const fetchSubscriptionStats = async () => {
      setSubscriptionStatsLoading(true);
      try {
        const response = await getSubscriptionStats();
        if (response?.success) {
          setSubscriptionStats(response.data || {});
        }
      } catch (error) {
        console.error("Error fetching subscription stats:", error);
      }
      setSubscriptionStatsLoading(false);
    };

    fetchSubscriptionStats();
  }, []);

  // Fetch projects with pagination
  const fetchProjects = async (page = 1) => {
    try {
      const projectsRes = await getAllProjectsSuperAdmin(page, 10, { status: "active" });

      if (projectsRes?.success && projectsRes?.data) {
        const activeProjectsData = projectsRes.data.map((project, index) => {
          const formattedStatus = project.project_status ? (project.project_status.charAt(0).toUpperCase() + project.project_status.slice(1).toLowerCase()) : 'Inactive';
          return {
            id: project.id || index,
            name: project.name || project.title || `Project ${index + 1}`,
            selected: true,
            type: "line",
            data: project.monthlyData || [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            recent_active: project.recent_active,
            status: formattedStatus,
            isActive: formattedStatus === 'Active',
            color: getProjectColor(project.id || index),
            organizationName: project.Organization?.title || 'N/A',
            createdAt: project.createdAt || new Date().toISOString(),
          };
        });

        setActiveProjects([
          { id: "all", name: "All Active Projects", selected: true },
          ...activeProjectsData,
        ]);

        if (projectsRes.pagination) {
          setProjectsPagination(projectsRes.pagination);
        }
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  // Fetch organizations with pagination
  const fetchOrganizations = async (page = 1) => {
    try {
      const organizationsRes = await getAllOrganizations(page, 10, { is_active: "true" });

      if (organizationsRes?.success && organizationsRes?.data) {
        const organizationsStats = Array.isArray(subscriptionStats?.organizations)
          ? subscriptionStats.organizations
          : [];
        const orgsData = organizationsRes.data.map((org, index) => {
          const planDisplay = getOrganizationDisplayPlan(org, organizationsStats);
          return {
            id: org.id || index,
            name: org.title || org.name || `Organization ${index + 1}`,
            projectCount: Number(org.total_projects ?? org.project_count ?? org.projectCount ?? 0),
            color: getProjectColor(org.id || index),
            planName: planDisplay.code,
            planTitle: planDisplay.title,
            managerName: org.manager_name || org.username || 'N/A',
            isActive: org.recent_active === true || org.recent_active === 1,
            status: org.recent_active === true || org.recent_active === 1 ? 'Active' : 'Inactive'
          };
        });

        setActiveOrganizations(orgsData);

        if (organizationsRes.pagination) {
          setOrganizationsPagination(organizationsRes.pagination);
        }
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  useEffect(() => {
    if (subscriptionStats) {
      fetchOrganizations(organizationsPagination.currentPage || 1);
    }
  }, [subscriptionStats]);

  // Fetch top organizations for chart
  const fetchTopOrganizations = async () => {
    try {
      const response = await getTopOrganizations(10);
      console.log('Top Organizations Response:', response);

      if (response?.success && response?.data) {
        const topOrgs = response.data.map((org, index) => ({
          id: org.id || index,
          name: org.title || org.name || `Organization ${index + 1}`,
          projectCount: org.project_count || org.projectCount || 0,
          rank: org.rank || index + 1,
        }));

        setTopOrganizations(topOrgs);
      }
    } catch (error) {
      console.error("Error fetching top organizations:", error);
    }
  };

  // Fetch super admin dashboard data
  const fetchSuperAdminData = async () => {
    setLoader(true);
    try {
      const [
        totalProjectsRes,
        allProjectsRes,
        organizationsRes,
        activeOrganizationsRes,
        managersRes,
        workersRes,
        topOrgsRes
      ] = await Promise.all([
        getAllProjectsSuperAdmin(1, 1).catch(err => ({ success: false, data: [] })),
        getAllProjectsSuperAdmin(1, 1000).catch(err => ({ success: false, data: [] })),
        getAllOrganizations(1, 1).catch(err => ({ success: false, data: [] })),
        getAllOrganizations(1, 10, { is_active: "true" }).catch(err => ({ success: false, data: [] })),
        getAllManagersSuperAdmin().catch(err => ({ success: false, data: [] })),
        getAllWorkersSuperAdmin().catch(err => ({ success: false, data: [] })),
        getTopOrganizations(10).catch(err => ({ success: false, data: [] })),
      ]);

      const totalCount = totalProjectsRes?.pagination?.totalItems || 0;

      setStatsData({
        total_organizations: organizationsRes?.pagination?.totalItems || 0,
        total_projects: totalCount,
        total_managers: managersRes?.pagination?.totalItems || 0,
        total_workers: workersRes?.pagination?.totalItems || 0,
      });

      if (allProjectsRes?.success && allProjectsRes?.data) {
        const allProjects = allProjectsRes.data;

        // Calculate counts for the 3 states
        const activeCount = allProjects.filter(p => (p.project_status || "").toLowerCase() === "active").length;
        const inactiveCount = allProjects.filter(p => (p.project_status || "").toLowerCase() === "inactive").length;
        const completedCount = allProjects.filter(p => (p.project_status || "").toLowerCase() === "completed").length;

        setProjectsChartData({
          totalProjects: totalCount,
          activeProjects: activeCount,
          inactiveProjects: inactiveCount,
          completedProjects: completedCount,
        });

        const activeProjectsData = allProjects.slice(0, 10).map((project, index) => {
          const formattedStatus = project.project_status ? (project.project_status.charAt(0).toUpperCase() + project.project_status.slice(1).toLowerCase()) : 'Inactive';
          return {
            id: project.id || index,
            name: project.name || project.title || `Project ${index + 1}`,
            selected: true,
            type: "line",
            data: project.monthlyData || [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            recent_active: project.recent_active,
            status: formattedStatus,
            isActive: formattedStatus === 'Active',
            color: getProjectColor(project.id || index),
            organizationName: project.Organization?.title || 'N/A',
            createdAt: project.createdAt || new Date().toISOString(),
          };
        });

        setActiveProjects([
          { id: "all", name: "All Active Projects", selected: true },
          ...activeProjectsData,
        ]);

        if (allProjectsRes.pagination) {
          setProjectsPagination({
            ...allProjectsRes.pagination,
            totalItems: totalCount
          });
        }

      }

      if (activeOrganizationsRes?.success && activeOrganizationsRes?.data) {
        const totalOrgCount = organizationsRes?.pagination?.totalItems || 0;
        const activeOrgCount = activeOrganizationsRes?.pagination?.totalItems || 0;
        const inactiveOrgCount = totalOrgCount - activeOrgCount;

        setOrganizationsChartData({
          totalOrganizations: totalOrgCount,
          activeOrganizations: activeOrgCount,
          inactiveOrganizations: inactiveOrgCount,
        });

        const organizationsStats = Array.isArray(subscriptionStats?.organizations)
          ? subscriptionStats.organizations
          : [];
        const orgsData = activeOrganizationsRes.data.map((org, index) => {
          const orgId = org.id || index;
          const planDisplay = getOrganizationDisplayPlan(org, organizationsStats);
          return {
            id: orgId,
            name: org.title || org.name || `Organization ${index + 1}`,
            projectCount: Number(org.total_projects ?? org.project_count ?? org.projectCount ?? 0),
            color: getProjectColor(orgId),
            planName: planDisplay.code,
            planTitle: planDisplay.title,
            managerName: org.manager_name || org.username || 'N/A',
            isActive: org.recent_active === true || org.recent_active === 1,
            status: org.recent_active === true || org.recent_active === 1 ? 'Active' : 'Inactive',
          };
        });

        setActiveOrganizations(orgsData);

        if (activeOrganizationsRes.pagination) {
          setOrganizationsPagination(activeOrganizationsRes.pagination);
        }
      }

      if (topOrgsRes?.success && topOrgsRes?.data?.length > 0) {
        const topOrgs = topOrgsRes.data.map((org) => ({
          id: org.org_id,
          name: org.title,
          projectCount: org.total_projects,
          rank: org.rank,
          managerName: org.manager_name,
          managerEmail: org.manager_email,
        }));

        setTopOrganizations(topOrgs);
      }

      setLoader(false);
    } catch (error) {
      console.error("Error fetching super admin data:", error);
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchSuperAdminData();
  }, []);

  const statsCards = [
    {
      id: 1,
      text: "Total Organizations",
      total: statsData.total_organizations,
      icon: TotalOrganizations,
      route: "/super-admin/subscription-management",
    },
    {
      id: 2,
      text: "Total Projects",
      total: statsData.total_projects,
      icon: TotalProjects,
      route: "/super-admin/projects",
    },
    {
      id: 3,
      text: "Total Managers",
      total: statsData.total_managers,
      icon: TotalManagers,
      route: "/super-admin/managers",
    },
    {
      id: 4,
      text: "Total Workers",
      total: statsData.total_workers,
      icon: TotalWorkers,
      route: "#",
    },
  ];

  const handleCheckboxChange = (id) => {
    setActiveProjects((prevProjects) => {
      if (id === "all") {
        return prevProjects.map((project) => ({
          ...project,
          selected: true,
        }));
      }

      return prevProjects.map((project) => {
        if (project.id === "all") {
          return { ...project, selected: false };
        }
        return project.id === id
          ? { ...project, selected: !project.selected }
          : project;
      });
    });
  };

  return loader ? (
    <div className="w-full h-screen flex justify-center items-center">
      <Spin />
    </div>
  ) : (
    <>
      {/* Organisation Quick-View Modal */}
      {selectedOrg && (
        <OrgModal
          org={selectedOrg}
          onClose={() => setSelectedOrg(null)}
          onViewFull={(id) => {
            setSelectedOrg(null);
            navigate("/super-admin/subscription-management", {
              state: { organizationId: id },
            });
          }}
        />
      )}

      <div className="w-full flex justify-center items-center flex-col gap-4 py-6">
        {/* Header */}
        <div className="w-full flex items-center justify-between px-3 pb-4 -mt-2">
          <p className="font-semibold text-[#1E3A8A] text-[20px]">
            Super Admin Dashboard
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="w-full grid grid-cols-4 gap-4 -mt-3 px-3">
          {statsCards.map(({ id, text, total, icon, route }) => (
            <div key={id} className="w-full">
              <Link to={route}>
                <div className="rounded-[12px] w-full h-[110px] bg-white shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02] flex items-center justify-between border border-gray-100">
                  <div className="px-4 w-full">
                    <div className="flex justify-between items-center">
                      <div className="dashboard_icon opacity-80 hover:opacity-100 transition-opacity">{icon}</div>
                      <div className="flex flex-col items-end">
                        <p className="font-bold text-[32px] text-[#1E3A8A] leading-tight">
                          {total}
                        </p>
                        <p className="font-semibold text-[13px] text-[#6B7280] mt-1">
                          {text}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* Active Projects */}
        <div className="w-full flex items-center px-3 gap-4 mt-3">
          {/* Projects Status Pie Chart */}
          <div className="w-[60%] h-[448px] bg-white rounded-[8px] py-4 px-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-full mb-4">
              <p className="text-lg font-bold text-[#1E3A8A]">
                Projects Status Overview
              </p>
            </div>
            <div className="w-full h-[calc(100%-50px)]">
              <ModernPieChart
                data={[
                  { title: 'Active', value: projectsChartData.activeProjects, total_employees: projectsChartData.activeProjects },
                  { title: 'Completed', value: projectsChartData.completedProjects, total_employees: projectsChartData.completedProjects },
                  { title: 'Inactive', value: projectsChartData.inactiveProjects, total_employees: projectsChartData.inactiveProjects }
                ]}
                centerTitle="Total"
                centerValue={projectsChartData.totalProjects}
                colors={["#3B82F6", "#10B981", "#EF4444"]}
              />
            </div>
          </div>

          {/* All Projects List */}
          <div className="w-[40%] h-[448px] flex flex-col gap-2 bg-white rounded-[8px] shadow-sm hover:shadow-md transition-shadow">
            <div className="w-full flex items-center justify-between px-4 pb-3 pt-4 border-b-2 border-[#F3F4F6]">
              <p className="font-bold text-[#1E3A8A] text-base">
                Projects
              </p>
              <Link to="/super-admin/projects" className="text-[13px] text-[#3B82F6] font-semibold hover:text-[#2563EB] cursor-pointer transition-colors">
                View All →
              </Link>
            </div>

            <div className="flex-1 px-4 overflow-y-auto custom-scrollbar">
              {activeProjects.filter(p => p.id !== "all").length === 0 ? (
                <div className="w-full py-8 text-center text-[#9CA3AF] text-sm">
                  No projects found
                </div>
              ) : (
                <div className="space-y-3 py-3 pb-4">
                  {activeProjects.filter(p => p.id !== "all").slice(0, 10).map((project, index) => (
                    <div
                      key={project.id}
                      onClick={() => navigate(`/super-admin/project-details/${project.id}`)}
                      className="flex items-center justify-between cursor-pointer hover:bg-[#F9FAFB] p-2 rounded-lg transition-all group"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-[40px] h-[40px] rounded-lg flex justify-center items-center text-white bg-gradient-to-br from-[#F97316] to-[#EA580C] shadow-sm group-hover:shadow-md transition-shadow"
                        >
                          <span className="text-sm font-bold">
                            {project.name.substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-[14px] text-[#1F2937] font-semibold group-hover:text-[#F97316] transition-colors">
                            {project.name}
                          </p>
                          <p className="text-[12px] text-[#9CA3AF] mt-0.5">
                            {project.status}
                          </p>
                        </div>
                      </div>
                      <div className={`w-2 h-2 rounded-full ${project.status === 'Active' ? 'bg-[#3B82F6]' : project.status === 'Completed' ? 'bg-[#10B981]' : 'bg-[#EF4444]'
                        } opacity-0 group-hover:opacity-100 transition-opacity shadow-sm`}></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Active Organizations */}
        <div className="w-full flex items-center px-3 gap-4">
          <div className="w-[60%] h-[500px] bg-white rounded-[8px] py-4 pr-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-full mb-4 px-4">
              <p className="text-lg font-bold text-[#1E3A8A]">
                Organizations with the most Projects
              </p>
            </div>
            <div className="w-full h-[calc(100%-70px)]">
              <TopOrganizationsChart
                organizations={topOrganizations}
              />
            </div>
          </div>

          {/* Organizations List */}
          <div className="w-[40%] h-[500px] flex flex-col gap-2 bg-white rounded-[8px] shadow-sm hover:shadow-md transition-shadow">
            <div className="w-full flex items-center justify-between px-4 pb-3 pt-4 border-b-2 border-[#F3F4F6]">
              <p className="font-bold text-[#1E3A8A] text-base">
                Organizations
              </p>
              <Link to="/super-admin/subscription-management" className="text-[13px] text-[#3B82F6] font-semibold hover:text-[#2563EB] cursor-pointer transition-colors">
                View All →
              </Link>
            </div>

            <div className="flex-1 px-4 overflow-y-auto custom-scrollbar">
              {activeOrganizations.length === 0 ? (
                <div className="w-full py-8 text-center text-[#9CA3AF] text-sm">
                  No organizations found
                </div>
              ) : (
                <div className="space-y-3 py-3 pb-4">
                  {activeOrganizations.slice(0, 10).map((org) => (
                    <div
                      key={org.id}
                      onClick={() => setSelectedOrg(org)}
                      className="flex items-center justify-between cursor-pointer hover:bg-[#F9FAFB] p-2 rounded-lg transition-all group"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          className="w-[40px] h-[40px] rounded-lg flex justify-center items-center text-white bg-gradient-to-br from-[#F97316] to-[#EA580C] shadow-sm group-hover:shadow-md transition-shadow flex-shrink-0"
                        >
                          <span className="text-sm font-bold">
                            {org.name.substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[14px] text-[#1F2937] font-semibold group-hover:text-[#F97316] transition-colors truncate">
                            {org.name}
                          </p>
                          <p className="text-[12px] text-[#9CA3AF] mt-0.5">
                            {org.projectCount} projects • <span className="capitalize">{org.planName}</span>
                          </p>
                        </div>
                      </div>
                      <div className={`w-2 h-2 rounded-full ${org.status === 'Active' ? 'bg-[#10B981]' : 'bg-[#EF4444]'
                        } opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 shadow-sm`}></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Monthly Revenue Overview */}
        <div className="w-full px-3">
          <SuperAdminRevenueChart
            loading={subscriptionStatsLoading}
            monthlyData={
              subscriptionStats?.monthlySummary ||
              subscriptionStats?.monthlyRevenue ||
              subscriptionStats?.monthlyRevenueData ||
              []
            }
          />
        </div>
      </div>
    </>
  );
};

export default SuperAdminDashboard;
